'use client';

import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { IconButton } from '@/app/components/buttons/IconButton';
import { Input } from '@/app/components/form/Input';
import { InfinityAsyncSelector } from '@/app/components/form/Selector/InfinityAsyncSelector';
import { SingleDatePicker } from '@/app/components/form/SingleDatePicker';
import type { EventFrequencyData } from '@/types/clientDetailsApiTypes';
import type { Nullable } from '@/types/common';
import { formatDateToMMDDYYYY, formatMMDDYYYYToYYYYMMDD } from '@/utils/formatDate';
import type {
  ImportantDatesFormValues,
  ImportantDateFormRow,
  EventFrequencyType,
} from '../../../sections/importantDatesSection/types';
import PlusIcon from '/public/icons/plus.svg';
import TrashIcon from '/public/icons/trash.svg';

const basePath = 'important_dates';

export const EditImportantDatesForm = () => {
  const {
    control,
    register,
    getValues,
    formState: { errors },
  } = useFormContext<ImportantDatesFormValues>();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: basePath,
  });

  const isAllowAddImportantDate = useMemo(() => {
    return fields.every((field) => field.id);
  }, [fields]);

  const handleAddNew = () => {
    append(
      {
        id: null,
        name: '',
        event_date: '',
        event_frequency_id: null,
        notes: null,
      } as ImportantDateFormRow,
      { shouldFocus: false }
    );
  };

  const handleOnChangeEventFrequency = useCallback(
    (fieldIndex: number, eventFrequencyId: Nullable<number> | undefined) => {
      const current = getValues(basePath)[fieldIndex];
      update(fieldIndex, { ...current, event_frequency_id: eventFrequencyId ?? null });
    },
    [getValues, update]
  );

  const handleOnChangeEventDate = useCallback(
    (fieldIndex: number, eventDate: string) => {
      const current = getValues(basePath)[fieldIndex];
      update(fieldIndex, { ...current, event_date: formatMMDDYYYYToYYYYMMDD(eventDate) ?? '' });
    },
    [getValues, update]
  );

  const handleOnChangeName = useCallback(
    (fieldIndex: number, name: string) => {
      const current = getValues(basePath)[fieldIndex];
      update(fieldIndex, { ...current, name });
    },
    [getValues, update]
  );

  const mapOptions = useCallback((options: EventFrequencyData[]) => {
    return options.map((option) => ({
      label: option.description,
      value: option.id,
    }));
  }, []);

  return (
    <div className="flex flex-col gap-4 pb-2">
      <div className="flex flex-col gap-4 w-full">
        {fields.map((field, fieldIndex) => {
          const shouldShowBorder = fields.length > 1 && fieldIndex < fields.length - 1;
          const isDeleteButtonVisible = field.event_frequency_id && field.name && field.event_date && fields.length > 1;
          return (
            <div
              key={field.id}
              className={`flex gap-4 items-end flex-wrap${shouldShowBorder ? ' border-b border-grey-400 py-5 max-md:py-4' : ''}`}
            >
              <div className="flex w-full gap-2 items-end">
                <Input
                  className="w-full"
                  autoComplete="off"
                  placeholder="Enter name/event"
                  {...register(`${basePath}.${fieldIndex}.name`)}
                  onBlur={(e) => handleOnChangeName(fieldIndex, e.target.value)}
                  intent="primaryWhite"
                  label="Name/Event"
                  errors={errors}
                />
                {isDeleteButtonVisible ? (
                  <div className="shrink-0">
                    <IconButton
                      type="button"
                      size="xl"
                      icon={<TrashIcon className="w-4 h-4" />}
                      onClick={() => remove(fieldIndex)}
                      color="danger"
                    />
                  </div>
                ) : null}
              </div>
              <div
                className={clsx('flex w-full gap-2 items-end', {
                  'pr-[52px]': isDeleteButtonVisible,
                })}
              >
                <Controller
                  name={`${basePath}.${fieldIndex}.event_date`}
                  control={control}
                  render={({ field: dateField }) => (
                    <SingleDatePicker
                      name={dateField.name}
                      label="Date"
                      intent="primaryWhite"
                      className="flex-1"
                      errors={errors}
                      value={formatDateToMMDDYYYY(dateField.value as string) || ''}
                      onChangeDate={(value) => handleOnChangeEventDate(fieldIndex, value)}
                    />
                  )}
                />
                <InfinityAsyncSelector<EventFrequencyType>
                  name={`${basePath}.${fieldIndex}.event_frequency_id`}
                  onChange={(option) => {
                    handleOnChangeEventFrequency(fieldIndex, option?.value as number);
                  }}
                  label="Frequency"
                  placeholder="Select"
                  value={field.event_frequency_id ?? ''}
                  url="/api/event-frequency"
                  mapOptions={mapOptions}
                  queryKey={['event-frequencies']}
                  className="flex-1"
                  errors={errors}
                />
              </div>
            </div>
          );
        })}
      </div>
      <BaseButton
        onClick={handleAddNew}
        type="button"
        size="ghost_small"
        startIcon={<PlusIcon />}
        intent="ghost"
        disabled={!isAllowAddImportantDate}
        className={clsx('w-fit', {
          'text-blue-300': isAllowAddImportantDate,
          'text-grey-700 cursor-not-allowed': !isAllowAddImportantDate,
        })}
      >
        Add New
      </BaseButton>
    </div>
  );
};